import React, { PropsWithChildren, useState } from "react";
import Group from "@/components/atoms/Group";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";
import { useTranslation } from "react-i18next";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import CTA from "@/components/atoms/CTA";
import { postConfirmTransportCompanyChoice, useGetFullLoadPriceOffers } from "@/services/Orders";
import { useMutation, useQueries } from "@tanstack/react-query";
import { getCompanyByIdPublic } from "@/services/Company";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";

interface FullLoadTransportTaskProps extends PropsWithChildren {
  transactionId: number;
  onClose: () => void;
}

const FullLoadTransportTask = (props: FullLoadTransportTaskProps) => {
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const { t } = useTranslation();

  const { data: fullLoadPriceData } = useGetFullLoadPriceOffers(props.transactionId);

  const priceOffers = useQueries({
    queries: fullLoadPriceData
      ? fullLoadPriceData.priceOffers.map((offer) => ({
          queryKey: ["getCompanyNamesFullLoad", offer],
          queryFn: () => getCompanyByIdPublic(offer.companyId),
          staleTime: Infinity,
        }))
      : [],
    combine: (results) => {
      return {
        fullPriceOffers: fullLoadPriceData
          ? fullLoadPriceData.priceOffers.map((offer) => {
              return {
                ...offer,
                offer: `+${offer.price} NOK | ${results.find((result) => result.data?.data.id === offer.companyId)?.data?.data.companyName}`,
              };
            })
          : [],
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const { mutate: mutateConfirmOffer, isPending: isPendingConfirmation } = useMutation({
    mutationFn: async (companyId: number | null) => {
      await postConfirmTransportCompanyChoice(props.transactionId, companyId);
    },
    onSuccess: () => {
      props.onClose();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.deleteError"), t("notifications:tasks.confirmOfferError"))
      );
    },
  });

  const handleCancel = () => {
    mutateConfirmOffer(null);
  };

  const handleConfirm = () => {
    // Mantine Select.onChange gives the option's string value, not an option object.
    mutateConfirmOffer(selectedCompany ? Number(selectedCompany) : null);
  };

  return (
    <Stack className="full flex col between h-100 py-10">
      <Group gap="20px" align="center" className={styles.taskHeader}>
        <SVGAsset name="yellow-exclamation" />
        <Text>{t("tasks:remainingTasks")}</Text>
      </Group>
      <Stack className={styles.tabPanelPadded}>
        <Text>{`${t("tasks:youRecieved")} ${fullLoadPriceData ? fullLoadPriceData?.priceOffers.length : 0} ${t("tasks:transportOffer")}`}</Text>
        <Select
          data-testid="transport-task-offer-select"
          data={transformDataToSelectOption(priceOffers.fullPriceOffers, "companyId", "offer")}
          value={selectedCompany}
          onChange={(value: any) => setSelectedCompany(value)}
        />
      </Stack>
      <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="flex-end">
        <Group justify="end">
          <CTA
            data-testid="transport-task-cancel-btn"
            intent="secondary"
            onClick={handleCancel}
            loading={isPendingConfirmation}
          >
            {t("actions:cancel")}
          </CTA>
          <CTA
            data-testid="transport-task-confirm-btn"
            disabled={!selectedCompany}
            loading={isPendingConfirmation}
            onClick={handleConfirm}
          >
            {t("actions:confirmChoice")}
          </CTA>
        </Group>
      </Group>
    </Stack>
  );
};

export default FullLoadTransportTask;
